import { CardModel } from '../interactive-paycard/shared/card-model';
import { stripDocumentMask } from './document-mask';

const ZENRISE_SDK_URL = 'https://cdn.zenrise.io/libraries/js/sdk/zenrise-sdk-latest.js';
const ZENRISE_SDK_VERSION = 3;

export type ZenriseSdkEnvironment = 'PROD' | 'DEV';

export type ZenriseCardToken = {
  bin: string;
  token: string;
  lastFourDigits: string;
};

type ZenriseSdk = {
  getCardToken(cardData: Record<string, unknown>): Promise<ZenriseCardToken>;
};

type ZenriseSdkConstructor = new (
  environment: ZenriseSdkEnvironment,
  version: number,
) => ZenriseSdk;

declare global {
  interface Window {
    Zenrise?: { Sdk: ZenriseSdkConstructor };
  }
}

let sdkLoading: Promise<ZenriseSdkConstructor> | null = null;

const loadZenriseSdk = (): Promise<ZenriseSdkConstructor> => {
  const loadedSdk = window.Zenrise?.Sdk;
  if (loadedSdk) {
    return Promise.resolve(loadedSdk);
  }
  if (!sdkLoading) {
    sdkLoading = new Promise<ZenriseSdkConstructor>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = ZENRISE_SDK_URL;
      script.async = true;
      script.onload = () => {
        const sdk = window.Zenrise?.Sdk;
        if (sdk) {
          resolve(sdk);
        } else {
          reject(new Error('Zenrise SDK did not register'));
        }
      };
      script.onerror = () => {
        reject(new Error('Zenrise SDK could not be loaded'));
      };
      document.head.appendChild(script);
    }).catch(error => {
      sdkLoading = null;
      throw error;
    });
  }
  return sdkLoading;
};

export const createZenriseCardToken = async (
  card: CardModel,
  environment: ZenriseSdkEnvironment,
): Promise<ZenriseCardToken> => {
  const ZenriseSdk = await loadZenriseSdk();
  return new ZenriseSdk(environment, ZENRISE_SDK_VERSION).getCardToken({
    card_number: (card.cardNumber ?? '').replace(/ /g, ''),
    card_expiration_month: `${card.expirationMonth}`,
    card_expiration_year: (card.expirationYear ?? '').substring(2, 4),
    card_holder_name: card.cardName ?? '',
    card_holder_identification: {
      type: card.documentType ? card.documentType.toLowerCase() : 'dni',
      number: stripDocumentMask(card.dni ?? ''),
    },
  });
};
